import React, { useState } from "react";
import axios from "axios";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Sparkles, ChevronRight, CheckCircle2, Calendar, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const INDUSTRIES = [
  "Healthcare", "SaaS / Software", "Professional services",
  "Financial services", "Retail / E-commerce", "Hospitality / Restaurants",
  "Real estate", "Education", "Manufacturing", "Logistics", "Other",
];

/**
 * ShowcaseInboundLeadForm — consultative demo-request capture for the
 * public /showcase/* page. Premium, lightweight, mobile-first. Calls
 * POST /api/public/showcase/{slug}/inbound-lead (no auth).
 */
export default function ShowcaseInboundLeadForm({ slug, source = "showcase", chatbotUrl }) {
  const [form, setForm] = useState({
    name: "",
    company: "",
    role: "",
    email: "",
    website: "",
    industry: "",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");

  const onChange = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  async function onSubmit(e) {
    e?.preventDefault?.();
    setError("");
    if (!form.name.trim() || !form.email.trim() || !form.company.trim()) {
      setError("Name, work email, and company are required.");
      return;
    }
    setSubmitting(true);
    try {
      const utm = {};
      const sp = new URLSearchParams(window.location.search);
      ["utm_source", "utm_medium", "utm_campaign", "utm_content", "utm_term"].forEach((k) => {
        const v = sp.get(k); if (v) utm[k] = v;
      });
      const r = await axios.post(`${API}/public/showcase/${slug}/inbound-lead`, {
        ...form,
        source,
        referrer: document.referrer || "",
        utm,
      });
      setResult(r.data);
    } catch (err) {
      const msg = err?.response?.data?.detail || "Something went wrong. Please try again.";
      setError(typeof msg === "string" ? msg : "Submission failed.");
    } finally {
      setSubmitting(false);
    }
  }

  if (result?.ok) {
    return (
      <div
        className="max-w-2xl mx-auto bg-gradient-to-br from-emerald-400/15 to-emerald-300/5 border border-emerald-300/30 backdrop-blur rounded-2xl p-8 sm:p-10 text-center"
        style={{ animation: "scaleIn 0.5s ease-out" }}
        data-testid="inbound-success"
      >
        <style>{`@keyframes scaleIn { from { opacity: 0; transform: scale(0.96) } to { opacity: 1; transform: scale(1) } }`}</style>
        <div className="w-14 h-14 mx-auto mb-4 rounded-full bg-emerald-400/20 grid place-items-center">
          <CheckCircle2 className="w-7 h-7 text-emerald-300"/>
        </div>
        <h3 className="font-display text-2xl sm:text-3xl font-semibold text-white leading-tight">
          You're on the list.
        </h3>
        <p className="text-white/80 mt-3 max-w-lg mx-auto text-sm sm:text-base" data-testid="inbound-success-msg">
          {result.thank_you}
        </p>
        <div className="flex flex-wrap gap-3 justify-center mt-6">
          {result.calendar_url && (
            <a href={result.calendar_url} target="_blank" rel="noreferrer" data-testid="inbound-calendar-link">
              <Button size="lg" className="bg-emerald-400 text-emerald-950 hover:bg-emerald-300 font-medium">
                <Calendar className="w-4 h-4 mr-2"/> Book a 30-min slot
              </Button>
            </a>
          )}
          {chatbotUrl && (
            <a href={chatbotUrl} target="_blank" rel="noreferrer">
              <Button size="lg" variant="outline" className="border-white/40 text-white hover:bg-white/10 hover:text-white">
                Explore the live demo <ChevronRight className="w-4 h-4 ml-1"/>
              </Button>
            </a>
          )}
        </div>
        <p className="text-white/50 text-xs mt-6">
          Reference ID: <span className="font-mono">{result.id?.slice(0, 8)}</span>
        </p>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto" data-testid="inbound-lead-form-wrap">
      <div className="text-center mb-6">
        <div className="inline-flex items-center gap-2 px-3 py-1 bg-emerald-400/15 border border-emerald-300/40 rounded-full text-emerald-200 text-xs uppercase tracking-[0.3em] mb-4">
          <Sparkles className="w-3.5 h-3.5"/> Built for your business
        </div>
        <h2 className="font-display text-2xl sm:text-4xl font-semibold leading-tight text-white" data-testid="inbound-headline">
          Experience Rudras-AI for Your Business
        </h2>
        <p className="text-white/70 mt-3 max-w-xl mx-auto text-sm sm:text-base">
          Share a few details and our team will configure a personalised AI Sales Assistant — trained on your own product, pricing, and ideal customer profile. Complimentary, no commitment.
        </p>
      </div>

      <form
        onSubmit={onSubmit}
        noValidate
        className="bg-white/[0.06] border border-white/10 backdrop-blur rounded-2xl p-5 sm:p-8 shadow-2xl"
        data-testid="inbound-lead-form"
      >
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <Label htmlFor="il-name" className="text-white/80 text-xs uppercase tracking-wider">Your name</Label>
            <Input
              id="il-name"
              value={form.name}
              onChange={onChange("name")}
              placeholder="Amelia Chen"
              required
              className="mt-1.5 bg-white/5 border-white/15 text-white placeholder:text-white/30 focus-visible:ring-emerald-400/40"
              data-testid="inbound-name"
            />
          </div>
          <div>
            <Label htmlFor="il-company" className="text-white/80 text-xs uppercase tracking-wider">Company</Label>
            <Input
              id="il-company"
              value={form.company}
              onChange={onChange("company")}
              placeholder="Northern Health Group"
              required
              className="mt-1.5 bg-white/5 border-white/15 text-white placeholder:text-white/30 focus-visible:ring-emerald-400/40"
              data-testid="inbound-company"
            />
          </div>
          <div>
            <Label htmlFor="il-role" className="text-white/80 text-xs uppercase tracking-wider">Role / Title</Label>
            <Input
              id="il-role"
              value={form.role}
              onChange={onChange("role")}
              placeholder="VP Sales · Founder · Marketing Lead"
              className="mt-1.5 bg-white/5 border-white/15 text-white placeholder:text-white/30 focus-visible:ring-emerald-400/40"
              data-testid="inbound-role"
            />
          </div>
          <div>
            <Label htmlFor="il-email" className="text-white/80 text-xs uppercase tracking-wider">Work email</Label>
            <Input
              id="il-email"
              type="email"
              value={form.email}
              onChange={onChange("email")}
              placeholder="amelia@northernhealth.com"
              required
              className="mt-1.5 bg-white/5 border-white/15 text-white placeholder:text-white/30 focus-visible:ring-emerald-400/40"
              data-testid="inbound-email"
            />
          </div>
          <div>
            <Label htmlFor="il-website" className="text-white/80 text-xs uppercase tracking-wider">Website</Label>
            <Input
              id="il-website"
              value={form.website}
              onChange={onChange("website")}
              placeholder="https://northernhealth.com"
              className="mt-1.5 bg-white/5 border-white/15 text-white placeholder:text-white/30 focus-visible:ring-emerald-400/40"
              data-testid="inbound-website"
            />
          </div>
          <div>
            <Label htmlFor="il-industry" className="text-white/80 text-xs uppercase tracking-wider">Industry</Label>
            <select
              id="il-industry"
              value={form.industry}
              onChange={onChange("industry")}
              className="mt-1.5 w-full bg-white/5 border border-white/15 text-white rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-400/40 focus:border-emerald-400/40"
              data-testid="inbound-industry"
            >
              <option value="" className="bg-stone-900">Select an industry…</option>
              {INDUSTRIES.map((i) => (
                <option key={i} value={i} className="bg-stone-900">{i}</option>
              ))}
            </select>
          </div>
        </div>

        <div className="mt-4">
          <Label htmlFor="il-message" className="text-white/80 text-xs uppercase tracking-wider">
            What would you like your AI Sales Assistant to help with? <span className="text-white/40 normal-case tracking-normal">(optional)</span>
          </Label>
          <Textarea
            id="il-message"
            value={form.message}
            onChange={onChange("message")}
            placeholder="e.g. qualify inbound leads, answer pricing questions, hand off to sales when ready"
            rows={3}
            className="mt-1.5 bg-white/5 border-white/15 text-white placeholder:text-white/30 focus-visible:ring-emerald-400/40 resize-none"
            data-testid="inbound-message"
          />
        </div>

        {error && (
          <div className="mt-4 px-3 py-2 bg-rose-500/15 border border-rose-400/30 rounded-lg text-rose-200 text-sm" data-testid="inbound-error">
            {error}
          </div>
        )}

        <div className="mt-6 flex flex-col sm:flex-row sm:items-center gap-3 sm:justify-between">
          <p className="text-white/50 text-xs">
            We'll only use this to set up your custom demo. No marketing spam.
          </p>
          <Button
            type="submit"
            size="lg"
            disabled={submitting}
            className="bg-emerald-400 text-emerald-950 hover:bg-emerald-300 font-medium w-full sm:w-auto"
            data-testid="inbound-submit"
          >
            {submitting ? (
              <><Loader2 className="w-4 h-4 mr-2 animate-spin"/> Submitting…</>
            ) : (
              <>Request Your Custom AI Sales Assistant <ChevronRight className="w-4 h-4 ml-1"/></>
            )}
          </Button>
        </div>
      </form>
    </div>
  );
}
